// Claude: RFX API — buyer creates/manages RFXs; sellers browse and respond 24-02-2026
import { api } from "./config/axiosConfig.js";
import { store } from "../redux/Store.ts";
import { errorResponseHandler, responseHandler } from "../Utils/handler.ts";

const getToken = () => store.getState().user?.token;

export const RfxAPI = {

  // ── BUYER ──────────────────────────────────────────────────────────────────

  // Buyer creates an RFX
  createRfx: async (data) => {
    try {
      const response = await api.request({
        url: "/rfx",
        method: "POST",
        data,
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Buyer lists their own RFXs (with response counts)
  getMyRfxs: async () => {
    try {
      const response = await api.request({
        url: "/rfx/my",
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Buyer views responses for a specific RFX
  getRfxResponses: async (rfxId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/responses`,
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Buyer toggles shortlist on a response
  toggleShortlist: async (rfxId, responseId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/responses/${responseId}/shortlist`,
        method: "PATCH",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: buyer marks seller response as not shortlisted — irreversible 18-04-2026
  markNotShortlisted: async (rfxId, responseId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/responses/${responseId}/not-shortlist`,
        method: "PATCH",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Buyer requests a detailed proposal from a shortlisted seller
  requestProposal: async (rfxId, responseId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/responses/${responseId}/request-proposal`,
        method: "PATCH",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Buyer closes an RFX — Claude: closeReason optional, shown to matched sellers 02-Apr-2026
  closeRfx: async (rfxId, closeReason) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/close`,
        method: "PATCH",
        data: { closeReason: closeReason || undefined },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: RFX dashboard stats 25-02-2026
  getBuyerRfxStats: async () => {
    try {
      const response = await api.request({
        url: "/rfx/buyer/stats",
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  getSellerRfxStats: async () => {
    try {
      const response = await api.request({
        url: "/rfx/seller/stats",
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // ── SELLER ─────────────────────────────────────────────────────────────────

  // Seller browses open RFXs
  browseRfxs: async () => {
    try {
      const response = await api.request({
        url: "/rfx/browse",
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: seller's keyword match history 10-03-2026
  getSellerKeywordMatches: async () => {
    try {
      const response = await api.request({
        url: "/rfx/seller/keyword-matches",
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Seller views a single RFX detail (with their own response if any)
  getRfxDetail: async (rfxId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}`,
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },


  // Seller marks not interested
  markNotInterested: async (rfxId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/not-interested`,
        method: "PATCH",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: markInterested now sends optional text; replaces respondToRfx + old markInterested 18-04-2026
  markInterested: async (rfxId, rfxResponseDetails = "") => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/interested`,
        method: "PATCH",
        data: { rfxResponseDetails },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: Buyer edits own RFX (only if open + no responses) 04-03-2026
  editRfx: async (rfxId, data) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}`,
        method: "PATCH",
        data,
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: N28 — buyer gets estimated seller reach count for given filter params 06-03-2026
  getRfxReachCount: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.targetCountry?.length) query.set('targetCountry', params.targetCountry.join(','));
      if (params.targetCity?.length) query.set('targetCity', params.targetCity.join(','));
      if (params.targetIndustries?.length) query.set('targetIndustries', params.targetIndustries.join(','));
      if (params.targetTags?.length) query.set('targetTags', params.targetTags.join(','));
      if (params.targetRelevanceStatus?.length) query.set('targetRelevanceStatus', params.targetRelevanceStatus.join(','));
      if (params.departments?.length) query.set('departments', params.departments.join(',')); // Claude: department filter 16-Apr-2026
      const response = await api.request({
        url: `/rfx/reach-count?${query.toString()}`,
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: content-based preview count — POST with full RFX params; Gemini extracts keywords server-side 08-May-2026
  getPreviewCount: async (params = {}) => {
    try {
      const response = await api.request({
        url: "/rfx/preview-count",
        method: "POST",
        data: params,
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: N22 — seller reports an RFX for review 03-03-2026
  reportRfx: async (rfxId, reason) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/report`,
        method: "PATCH",
        data: { reason },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: N22 — buyer reports a seller's response 03-03-2026
  reportRfxResponse: async (rfxId, responseId, reason) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/responses/${responseId}/report`,
        method: "PATCH",
        data: { reason },
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: rescanRFXforSeller — fire-and-forget on Dashboard/Opportunities load 01-Apr-2026
  rescanRFXforSeller: async () => {
    try {
      const response = await api.request({
        url: "/rfx/seller/rescan",
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      // silent — rescan failure should not affect UI
    }
  },

  // Claude: buyer fetches seller public profile + all assets for drawer 01-Apr-2026
  getSellerPublicProfile: async (sellerId) => {
    try {
      const response = await api.request({
        url: `/rfx/seller/${sellerId}/public-profile`,
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: Discover — buyer lists own discover requirements 31-03-2026
  getMyDiscoverRfxs: async () => {
    try {
      const response = await api.request({
        url: "/rfx/my",
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },

  // Claude: Discover — buyer gets matched seller results for a requirement 31-03-2026
  getRFXMatchedSellers: async (rfxId) => {
    try {
      const response = await api.request({
        url: `/rfx/${rfxId}/discover-results`,
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return responseHandler(response);
    } catch (err) {
      throw errorResponseHandler(err);
    }
  },
};
